/* OFC Apparatus Driver/Operator document manifest (#1579).
 *
 * The Driver/Operator section's items ARE the Office of the Fire Commissioner's own
 * numbered skill sheets — `Apparatus Driver Operator, Interior Operations`, Skills Sheet
 * Evaluations #1-#23 (EMBC/OFC, 2022), the package a department's evaluators sign. This
 * manifest maps each catalogue drill code (`do-N`, N = sheet number) to the PDF of that
 * sheet, plus the three written assessments and their answer keys.
 *
 * Same mirror shape as common/vieraSkillSheets.js — our stable `slug` is the public api
 * path, bytes live in a bucket (filesystem fallback for local dev), and the manifest here
 * is the only thing that decides which slugs exist. Two differences:
 *
 *  - There is no upstream URL to fetch from. OFC distributes this package to departments
 *    as Word documents; the PDFs are produced once from those originals and uploaded (see
 *    ofc-driver-operator/README.md). So there's no `file`/`sourceUrl` here.
 *
 *  - `restricted: true` documents are the exam papers and answer keys. They must NOT be
 *    served by the public route a candidate can hit — they're fetched through an authed
 *    api call gated on `requirements.approve`, i.e. the same permission that lets you
 *    sign an item off. Skill sheets themselves are deliberately open: they're the
 *    checklist a candidate is entitled to study before being evaluated against it.
 */

// Edition folder, mirroring vieraSkillSheets' layout: every document lives at
// <edition>/<slug>.pdf, so a complete snapshot of a revision is self-contained and a
// later revision can be added without touching this one.
const CURRENT_EDITION = 'ofc-do-2022';
const CURRENT_EDITION_LABEL = 'OFC Apparatus Driver/Operator (2022)';

// The document set is per-drill, so titles are derived from the catalogue rather than
// duplicated here — one row per sheet, keyed by the drill code it belongs to.
const sheet = (code, number) => ({
    slug: `ofc-${code}`,
    code,
    kind: 'skillSheet',
    title: `OFC Skills Sheet Evaluation #${number}`,
    restricted: false
});

const SHEETS = [...Array(23)].map((unused, i) => sheet(`do-${i + 1}`, i + 1));

// One written assessment per conditional branch (there is no EVD exam), each with an
// answer key. `exam` matches the exam id in common/skillDrillsDriverOperator.js.
const examDocs = (exam, label) => [
    {slug: `ofc-exam-${exam}`, exam, kind: 'exam', title: `${label} — Written Assessment`, restricted: true},
    {slug: `ofc-exam-${exam}-key`, exam, kind: 'answerKey', title: `${label} — Answer Key`, restricted: true}
];

const EXAMS = [
    ...examDocs(1, 'Pump'),
    ...examDocs(2, 'Aerial'),
    ...examDocs(3, 'Mobile Water Supply')
];

const OFCDriverOperatorSheets = [...SHEETS, ...EXAMS];

const bySlug = new Map(OFCDriverOperatorSheets.map(d => [d.slug, d]));

// One document by slug, or null. The api validates against this rather than a path
// regex, so only known documents can ever be streamed.
const getOFCDriverOperatorSheet = slug => bySlug.get(slug) || null;

// Storage/object path within an edition folder.
const ofcDriverOperatorSheetPath = (slug, edition = CURRENT_EDITION) => `${edition}/${slug}.pdf`;

// The skill sheet for one drill code, or null.
const ofcSheetForDrill = code => OFCDriverOperatorSheets.find(d => d.kind === 'skillSheet' && d.code === code) || null;

// The exam paper + answer key for one exam id. Both are restricted.
const ofcDocsForExam = exam => ({
    exam: OFCDriverOperatorSheets.find(d => d.kind === 'exam' && d.exam === exam) || null,
    answerKey: OFCDriverOperatorSheets.find(d => d.kind === 'answerKey' && d.exam === exam) || null
});

module.exports = {
    OFCDriverOperatorSheets,
    CURRENT_EDITION,
    CURRENT_EDITION_LABEL,
    getOFCDriverOperatorSheet,
    ofcDriverOperatorSheetPath,
    ofcSheetForDrill,
    ofcDocsForExam
};
